import React, {useEffect} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import closeImage from '@/assets/imgs/invitation/close.webp';
import bannerImage from '@/assets/imgs/invitation/refer-earn-rules-banner.webp';
import invitationTaskIcon from '@/assets/imgs/invitation/refer-earn-rules-invitation-task.webp';
import depositRebateIcon from '@/assets/imgs/invitation/refer-earn-rules-deposit-rebate.webp';
import betCommissionIcon from '@/assets/imgs/invitation/refer-earn-rules-bet-commission.webp';
import {AgentLevelGuide} from '../../invitation-rules-popup';
import {formatCashbackThreshold} from '../../commission-rule.utils';
import type {RechargeCommissionRuleThresholds} from '../../../invitation.type';

interface ReferEarnWheelRulesModalProps {
  visible: boolean;
  onClose: () => void;
  initialSection?: ReferEarnRulesSection;
  thresholds?: RechargeCommissionRuleThresholds;
}

export type ReferEarnRulesSection = 'invitation' | 'deposit' | 'bet';

const renderPortal = (node: React.ReactElement) =>
  typeof document === 'undefined' ? node : createPortal(node, document.body);

const sectionContent = [
  {
    title: 'invitation.wheelRules.sections.invitationTask',
    icon: invitationTaskIcon,
    depositThreshold: 'rechargeThreshold',
    validThreshold: 'betThreshold',
    intro: 'invitation.rulesPopup.sections.referral.intro',
    rules: [
      'invitation.rulesPopup.sections.referral.rule1',
      'invitation.rulesPopup.sections.referral.rule2',
      'invitation.rulesPopup.sections.referral.rule3',
    ],
  },
  {
    title: 'invitation.wheelRules.sections.depositRebate',
    icon: depositRebateIcon,
    depositThreshold: 'rechargeCommissionRechargeThreshold',
    validThreshold: 'rechargeCommissionBetThreshold',
    intro: 'invitation.rulesPopup.sections.deposit.intro1',
    extra: 'invitation.rulesPopup.sections.deposit.intro2',
    rules: [
      'invitation.rulesPopup.sections.deposit.rule1',
      'invitation.rulesPopup.sections.deposit.rule2',
    ],
  },
  {
    title: 'invitation.wheelRules.sections.betCommission',
    icon: betCommissionIcon,
    depositThreshold: 'betCommissionRechargeThreshold',
    validThreshold: 'betCommissionBetThreshold',
    intro: 'invitation.rulesPopup.sections.bet.intro1',
    extra: 'invitation.rulesPopup.sections.bet.intro2',
    rules: [
      'invitation.rulesPopup.sections.bet.rule1',
      'invitation.rulesPopup.sections.bet.rule2',
    ],
  },
] as const;

export function ReferEarnWheelRulesModal({
  visible,
  onClose,
  initialSection = 'invitation',
  thresholds,
}: ReferEarnWheelRulesModalProps) {
  const {t} = useTranslation();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = React.useState(visible);
  const [active, setActive] = React.useState(false);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [visible]);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setActive(true));
      return () => cancelAnimationFrame(frame);
    }
    setActive(false);
    const timer = window.setTimeout(() => setMounted(false), 180);
    return () => window.clearTimeout(timer);
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const scrollElement = scrollRef.current;
      const targetElement = scrollElement?.querySelector<HTMLElement>(
        `[data-rules-section="${initialSection}"]`,
      );
      if (!scrollElement || !targetElement) {
        return;
      }
      scrollElement.scrollTo({
        top: Math.max(
          0,
          targetElement.offsetTop -
            (scrollElement.clientHeight - targetElement.clientHeight) / 2,
        ),
        behavior: 'auto',
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [initialSection, mounted, visible]);

  if (!mounted) {
    return null;
  }

  return renderPortal(
    <div
      className={`fixed inset-0 z-[11140] flex items-center justify-center bg-black/70 px-4 py-4 transition-opacity duration-200 ${
        active ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      onClick={onClose}>
      <div
        className={`relative h-[80vh] max-h-[42rem] w-full max-w-[21.25rem] transition-transform duration-200 ease-out ${
          active ? 'scale-100' : 'scale-[0.97]'
        }`}
        onClick={event => event.stopPropagation()}>
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[0.9375rem] bg-[#F2F0F3]">
          <div className="flex h-7 w-full shrink-0 items-center justify-center gap-[0.125rem] px-3 font-inter text-sm font-bold leading-[1.0625rem] text-black">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true">
              <path
                d="M17.1321 5C17.6746 5 18.1949 5.2155 18.5785 5.5991C18.9621 5.9827 19.1776 6.50297 19.1776 7.04545V17.9545C19.1776 18.497 18.9621 19.0173 18.5785 19.4009C18.1949 19.7845 17.6746 20 17.1321 20H8.26847C7.78507 19.9999 7.3173 19.8287 6.94807 19.5167C6.57885 19.2047 6.33201 18.7721 6.25131 18.2955H7.07528C7.47589 18.2954 7.86059 18.1387 8.14718 17.8588C8.43376 17.5789 8.59949 17.198 8.60892 16.7975C8.61836 16.397 8.47077 16.0087 8.19769 15.7156C7.92461 15.4225 7.54771 15.2479 7.14756 15.229L7.07528 15.2273H6.22301V14.0341H7.07528C7.46964 14.0341 7.84885 13.8822 8.13419 13.61C8.41953 13.3378 8.58909 12.9662 8.60767 12.5723L8.60937 12.5C8.60937 12.1056 8.45751 11.7264 8.18531 11.4411C7.91311 11.1558 7.54147 10.9862 7.14756 10.9676L7.07528 10.9659H6.22301V9.77273H7.07528C7.46964 9.77273 7.84885 9.62086 8.13419 9.34866C8.41953 9.07646 8.58909 8.70482 8.60767 8.31091L8.60937 8.23864C8.60937 7.84428 8.45751 7.46507 8.18531 7.17973C7.91311 6.89439 7.54147 6.72483 7.14756 6.70625L7.07528 6.70455H6.25131C6.33201 6.22793 6.57885 5.79528 6.94807 5.48327C7.3173 5.17127 7.78507 5.00006 8.26847 5H17.1321ZM7.07528 16.25C7.21091 16.25 7.34097 16.3039 7.43687 16.3998C7.53277 16.4957 7.58665 16.6257 7.58665 16.7614C7.58665 16.897 7.53277 17.0271 7.43687 17.1229C7.34097 17.2188 7.21091 17.2727 7.07528 17.2727H5.37074C5.23512 17.2727 5.10505 17.2188 5.00915 17.1229C4.91325 17.0271 4.85938 16.897 4.85938 16.7614C4.85938 16.6257 4.91325 16.4957 5.00915 16.3998C5.10505 16.3039 5.23512 16.25 5.37074 16.25H7.07528ZM16.7571 9.125C16.6621 9.03001 16.5494 8.95466 16.4253 8.90326C16.3012 8.85185 16.1682 8.82539 16.0339 8.82539C15.8995 8.82539 15.7665 8.85185 15.6424 8.90326C15.5183 8.95466 15.4056 9.03001 15.3106 9.125L10.4111 14.0252C10.3055 14.1307 10.2375 14.268 10.2174 14.4159L10.0794 15.4359C10.0723 15.4884 10.0775 15.5418 10.0946 15.5918C10.1117 15.6419 10.1402 15.6874 10.178 15.7245C10.2157 15.7617 10.2615 15.7895 10.3119 15.8059C10.3622 15.8222 10.4157 15.8266 10.468 15.8187L11.4727 15.6674C11.6169 15.6458 11.7503 15.5785 11.8535 15.4755L16.7568 10.5718C16.9485 10.38 17.0562 10.1199 17.0562 9.84875C17.0562 9.57756 16.9485 9.31747 16.7568 9.12568L16.7571 9.125ZM7.07528 11.9886C7.21091 11.9886 7.34097 12.0425 7.43687 12.1384C7.53277 12.2343 7.58665 12.3644 7.58665 12.5C7.58665 12.6356 7.53277 12.7657 7.43687 12.8616C7.34097 12.9575 7.21091 13.0114 7.07528 13.0114H5.37074C5.23512 13.0114 5.10505 12.9575 5.00915 12.8616C4.91325 12.7657 4.85938 12.6356 4.85938 12.5C4.85938 12.3644 4.91325 12.2343 5.10505 12.1384C5.10505 12.0425 5.23512 11.9886 5.37074 11.9886H7.07528ZM7.07528 7.72727C7.21091 7.72727 7.34097 7.78115 7.43687 7.87705C7.53277 7.97295 7.58665 8.10301 7.58665 8.23864C7.58665 8.37426 7.53277 8.50433 7.43687 8.60022C7.34097 8.69612 7.21091 8.75 7.07528 8.75H5.37074C5.23512 8.75 5.10505 8.69612 5.00915 8.60022C4.91325 8.50433 4.85938 8.37426 4.85938 8.23864C4.85938 8.10301 4.91325 7.97295 5.00915 7.87705C5.10505 7.78115 5.23512 7.72727 5.37074 7.72727H7.07528Z"
                fill="black"
              />
            </svg>
            <span>{t('invitation.home.rules')}</span>
          </div>
          <div
            ref={scrollRef}
            className="min-h-0 flex-1 overflow-y-auto px-3 pb-5 pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div
              className="relative flex h-[4.5625rem] items-center justify-center overflow-hidden rounded-xl bg-[#F3CD5C] bg-cover bg-center px-4 text-center"
              style={{backgroundImage: `url(${bannerImage})`}}>
              <div className="relative z-[1] font-inter text-sm font-bold leading-[1.25rem] text-black">
                <div>{t('invitation.rulesPopup.banner.line1')}</div>
                <div>{t('invitation.rulesPopup.banner.line2')}</div>
              </div>
            </div>

            <section className="mt-[1.625rem] overflow-visible rounded-[0.625rem] pb-3">
              <AgentLevelGuide standalone light />
            </section>

            {sectionContent.map(section => (
              <section
                key={section.title}
                data-rules-section={
                  section.title ===
                  'invitation.wheelRules.sections.invitationTask'
                    ? 'invitation'
                    : section.title ===
                      'invitation.wheelRules.sections.depositRebate'
                    ? 'deposit'
                    : 'bet'
                }
                className="relative mt-[1.625rem] overflow-visible rounded-[0.625rem] bg-white shadow-[0_0.0625rem_0.25rem_rgba(0,0,0,0.18)]">
                <h2 className="absolute left-1/2 top-[-0.8505rem] z-10 m-0 flex h-[1.75rem] w-[14.25rem] -translate-x-1/2 items-center justify-center gap-4 rounded-[0.875rem] border border-[rgba(255,255,255,0.1)] bg-[#F4CF5E] px-4 text-center font-inter text-sm font-semibold leading-normal text-black">
                  <img
                    src={section.icon}
                    alt=""
                    className="h-[0.875rem] w-[1.0625rem] object-contain"
                  />
                  {t(section.title)}
                </h2>
                <div className="px-3 pb-3 pt-6 font-inter text-xs font-normal leading-4 text-[#303030]">
                  <p className="m-0 text-[#F55F5F]">{t(section.intro)}</p>
                  {'extra' in section && section.extra ? (
                    <p className="m-0 mt-2 text-[#F55F5F]">
                      {t(section.extra)}
                    </p>
                  ) : null}
                  <p className="m-0 mt-2">
                    {t('invitation.rulesPopup.common.requirements')}
                  </p>
                  <ul className="m-0 list-disc pl-5">
                    <li>
                      <span>
                        {t('invitation.rulesPopup.common.successfulDeposit')}
                      </span>
                      <span className="text-[#E79A00]">
                        ₹
                        {formatCashbackThreshold(
                          thresholds?.[section.depositThreshold],
                        ) || '-'}
                      </span>
                    </li>
                    <li>
                      {t(
                        section.title ===
                          'invitation.wheelRules.sections.depositRebate'
                          ? 'invitation.rulesPopup.common.validBettingAmount'
                          : 'invitation.rulesPopup.common.validTurnover',
                      )}
                      <span className="text-[#E79A00]">
                        ≥ ₹
                        {formatCashbackThreshold(
                          thresholds?.[section.validThreshold],
                        ) || '-'}
                      </span>
                    </li>
                  </ul>
                  <p className="m-0 mt-2">
                    {t('invitation.rulesPopup.common.rules')}
                  </p>
                  <ol className="m-0 list-decimal pl-5">
                    {section.rules.map(rule => (
                      <li key={rule}>{t(rule)}</li>
                    ))}
                  </ol>
                </div>
              </section>
            ))}
          </div>
        </div>
        <button
          type="button"
          aria-label={t('share.close')}
          onClick={onClose}
          className="absolute right-0 top-[-2.5rem] z-20 flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 active:scale-95">
          <img src={closeImage} alt="" className="size-6 object-contain" />
        </button>
      </div>
    </div>,
  );
}
